import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import { useStore } from "vuex";
  import MasterplanUpload from "@/components/masterplan/MasterplanUpload.vue";
  import LotMatcher from "@/components/masterplan/LotMatcher.vue";
  import StageDrawer from "@/components/masterplan/StageDrawer.vue";
  import PointsOfInterestEditor from "@/components/masterplan/PointsOfInterestEditor.vue";
  import EstateBoundaryDrawer from "@/components/masterplan/EstateBoundaryDrawer.vue";
  import ZoomLevelsEditor from "@/components/masterplan/ZoomLevelsEditor.vue";
  import interactiveMasterplanApi from "@/services/api/interactiveMasterplanApi";
  import estateApi from "@/services/api/estateApi";
  import lotApi from "@/services/api/lotApi";
  import stageApi from "@/services/api/stageApi";
  import { EstateType } from "@/enums/EstateType";
  import type {
    EstateLabelMetadata,
    ZoomCenter,
    ZoomLevels,
  } from "@/types/InteractiveMasterplanInterface";

  interface SvgElement {
    elementIndex: number;
    type: "rect" | "path" | "polygon" | "circle";
    id?: string;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    shapePath?: string;
    statusBarPath?: string;
    polygonPoints?: Array<{ x: number; y: number }>;
    curvePoints?: Array<{ edgeIndex: number; x: number; y: number }>;
    rawAttributes: Record<string, string>;
  }

  interface UploadResult {
    svgFile: File;
    elements: SvgElement[];
  }

  interface Assignment {
    elementIndex: number;
    type: string;
    lotId: string;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    shapePath?: string;
    statusBarPath?: string;
    polygonPoints?: Array<{ x: number; y: number }>;
    curvePoints?: Array<{ edgeIndex: number; x: number; y: number }>;
    lotNumberMetadata?: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      displayed: boolean;
    };
    lotSizeMetadata?: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      displayed: boolean;
    };
  }

  type Step =
    | "upload"
    | "match"
    | "stages"
    | "estate-boundary"
    | "poi"
    | "zoom"
    | "preview";

  interface StageAssignmentResult {
    stageId: string;
    boundaryPath: string;
    boundaryPoints?: Array<{ x: number; y: number }>;
    curvePoints?: Array<{ edgeIndex: number; x: number; y: number }>;
  }

  interface EnrichedAssignment extends Assignment {
    stageId?: string;
  }

  
export default /*@__PURE__*/_defineComponent({
  ...{ name: "AdminMasterplanEditor" },
  __name: 'AdminMasterplanEditor',
  setup(__props, { expose: __expose }) {
  __expose();

  
  const router = useRouter();
  const route = useRoute();
  const store = useStore();

  const estateId = ref(route.params.estateId as string);
  const wizardSteps: Array<{ key: Step; label: string }> = [
    { key: "upload", label: "Upload SVG" },
    { key: "match", label: "Assign Lots" },
    { key: "stages", label: "Assign Stages" },
    { key: "estate-boundary", label: "Estate Boundary" },
    { key: "zoom", label: "Set Zoom Levels" },
    { key: "poi", label: "Points of Interest" },
    { key: "preview", label: "Preview & Save" },
  ];
  const step = ref<Step>("upload");
  const currentStepIndex = computed(() =>
    wizardSteps.findIndex(({ key }) => key === step.value)
  );
  const goToPreviousStep = () => {
    if (currentStepIndex.value > 0)
      step.value = wizardSteps[currentStepIndex.value - 1].key;
  };

  const selectedEstate = ref<any>(null);
  const uploadResult = ref<UploadResult | null>(null);
  const assignments = ref<Assignment[]>([]);
  const estateLots = ref<any[]>([]);
  const estateStages = ref<any[]>([]);
  const stageAssignments = ref<StageAssignmentResult[]>([]);
  const enrichedAssignments = ref<EnrichedAssignment[]>([]);
  const pointsOfInterest = ref<any[]>([]);
  const lotNumberMetadata = ref<any[]>([]);
  const lotSizeMetadata = ref<any[]>([]);
  const stageLabelMetadata = ref<any[]>([]);
  const saving = ref(false);
  const showSuccess = ref(false);
  const existingMasterplanId = ref<string | null>(null);
  const showOverwriteConfirm = ref(false);
  const existingMasterplanData = ref<any>(null);
  const loadingExisting = ref(false);
  const initialAssignments = ref<Assignment[]>([]);
  const initialStageBoundaries = ref<
    Array<{
      stageId: string;
      boundaryPath: string;
      boundaryPoints?: Array<{ x: number; y: number }>;
      curvePoints?: Array<{ edgeIndex: number; x: number; y: number }>;
    }>
  >([]);
  const initialStageLabelMetadata = ref<any[]>([]);
  const estateBoundaryPath = ref<string | null>(null);
  const initialEstateBoundaryPath = ref<string | undefined>(undefined);
  const estateLabelMetadata = ref<EstateLabelMetadata | null>(null);
  const initialEstateLabelMetadata = ref<EstateLabelMetadata | undefined>(
    undefined
  );
  const zoomLevels = ref<ZoomLevels>({
    estateBoundary: 1,
    stages: 2,
    lots: 3,
  });
  const estateZoomCenter = ref<ZoomCenter | undefined>(undefined);
  const stageZoomCenter = ref<ZoomCenter | undefined>(undefined);

  const mapLots = (rawLots: any[]) =>
    rawLots.map((lot: any) => ({
      lotId: (lot.lotId ?? lot._id)?.toString(),
      lotTitle: lot.lotTitle ?? lot.title,
      lotStatus: lot.lotStatus ?? lot.status ?? "Not Released",
      stageId:
        typeof lot.stageId === "object"
          ? lot.stageId?._id?.toString()
          : lot.stageId?.toString(),
      stageTitle:
        lot.stageTitle ??
        (typeof lot.stageId === "object" ? lot.stageId?.title : ""),
      lotPrice: lot.lotPrice ?? lot.price,
      lotSize: lot.lotSize ?? lot.size,
    }));

  const handleUploadSuccess = async (result: UploadResult) => {
    console.log("Upload result:", result);
    uploadResult.value = result;
    step.value = "match";

    if (estateId.value) {
      try {
        const lotsResponse = await lotApi.getEstateLots({
          estateId: estateId.value,
          params: {},
        });

        console.log("Lots API response:", lotsResponse.data);

        if (Array.isArray(lotsResponse.data)) {
          estateLots.value = mapLots(lotsResponse.data);
        } else if (
          lotsResponse.data?.data &&
          Array.isArray(lotsResponse.data.data)
        ) {
          estateLots.value = mapLots(lotsResponse.data.data);
        } else {
          console.warn("Unexpected lots response format:", lotsResponse.data);
          estateLots.value = [];
        }

        console.log(
          "Estate lots loaded:",
          estateLots.value.length,
          estateLots.value
        );
      } catch (error) {
        console.error("Failed to load estate lots:", error);
        estateLots.value = [];
      }
    }
  };

  const handleAssignmentComplete = (data: {
    assignments: Assignment[];
    elements: SvgElement[];
    lotNumberMetadata?: any[];
    lotSizeMetadata?: any[];
  }) => {
    assignments.value = data.assignments;

    if (data.lotNumberMetadata)
      lotNumberMetadata.value = data.lotNumberMetadata;

    if (data.lotSizeMetadata) lotSizeMetadata.value = data.lotSizeMetadata;

    initialAssignments.value = [...data.assignments];

    if (uploadResult.value) {
      uploadResult.value.elements = data.elements;
    }

    step.value = "stages";
  };

  const handleStageComplete = (data: {
    stageAssignments: StageAssignmentResult[];
    updatedAssignments: EnrichedAssignment[];
    stageLabelMetadata?: any[];
  }) => {
    stageAssignments.value = data.stageAssignments;
    enrichedAssignments.value = data.updatedAssignments;
    initialStageBoundaries.value = [...data.stageAssignments];

    if (data.stageLabelMetadata) {
      stageLabelMetadata.value = data.stageLabelMetadata;
      initialStageLabelMetadata.value = [...data.stageLabelMetadata];
    }

    step.value = "estate-boundary";
  };

  const handleEstateBoundaryComplete = (data: {
    boundaryPath: string | null;
    estateLabelMetadata: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      logoUrl?: string;
      logoFileName?: string;
      labelText?: string;
      boundaryOpacity?: number;
    };
  }) => {
    estateBoundaryPath.value = data.boundaryPath;
    estateLabelMetadata.value = data.boundaryPath
      ? data.estateLabelMetadata
      : null;
    initialEstateBoundaryPath.value = data.boundaryPath || undefined;
    initialEstateLabelMetadata.value = data.boundaryPath
      ? { ...data.estateLabelMetadata }
      : undefined;
    step.value = "zoom";
  };

  const handlePoiComplete = (pois: any[]) => {
    pointsOfInterest.value = pois;
    step.value = "preview";
  };

  const zoomPreviewState = ref<{
    deviceMode: "desktop" | "mobile" | "landscape";
    activeView: "estateBoundary" | "stages" | "lots";
    mobileExpanded: boolean;
    selectedStageId: string;
  }>();
  const handleZoomComplete = (settings: {
    previewState: NonNullable<typeof zoomPreviewState.value>;
    zoomLevels: ZoomLevels;
    estateZoomCenter?: ZoomCenter;
    stageZoomCenter?: ZoomCenter;
  }) => {
    zoomPreviewState.value = settings.previewState;
    zoomLevels.value = settings.zoomLevels;
    estateZoomCenter.value = settings.estateZoomCenter;
    stageZoomCenter.value = settings.stageZoomCenter;
    step.value = "poi";
  };

  const handleSave = async () => {
    if (!uploadResult.value || !selectedEstate.value) return;

    saving.value = true;

    try {
      const formData = new FormData();

      formData.append("file", uploadResult.value.svgFile);
      formData.append("userId", store.state.user.data._id);
      formData.append("estateId", estateId.value);

      let coordinateViewBox = "";
      const svgText = await uploadResult.value.svgFile.text();
      const vbMatch = svgText.match(/<svg[^>]*\bviewBox=["']([^"']+)["']/);

      if (vbMatch) {
        coordinateViewBox = vbMatch[1];
      } else {
        const wMatch = svgText.match(/<svg[^>]*\bwidth=["']([^"']+)["']/);
        const hMatch = svgText.match(/<svg[^>]*\bheight=["']([^"']+)["']/);
        const absoluteValue = /^\d+(\.\d+)?(px|pt|pc|mm|cm|in|em|rem)?$/;

        if (
          wMatch &&
          hMatch &&
          absoluteValue.test(wMatch[1].trim()) &&
          absoluteValue.test(hMatch[1].trim())
        ) {
          coordinateViewBox = `0 0 ${parseFloat(wMatch[1])} ${parseFloat(
            hMatch[1]
          )}`;
        }
      }

      const uploadResponse = await interactiveMasterplanApi.uploadSvgFile(
        formData
      );
      const svgFileData = uploadResponse.data;

      const landownerId =
        typeof selectedEstate.value.landownerId === "string"
          ? selectedEstate.value.landownerId
          : selectedEstate.value.landownerId._id;

      const masterplanData = {
        estateId: estateId.value,
        landownerId: landownerId || store.state.user.data._id,
        svgFile: svgFileData,
        lots: (enrichedAssignments.value.length > 0
          ? enrichedAssignments.value
          : assignments.value
        ).map((a: EnrichedAssignment) => ({
          lotId: a.lotId,
          stageId: a.stageId,
          boundingBox: a.boundingBox,
          elementIndex: a.elementIndex,
          ...(a.shapePath ? { shapePath: a.shapePath } : {}),
          ...(a.statusBarPath ? { statusBarPath: a.statusBarPath } : {}),
          ...(a.polygonPoints ? { polygonPoints: a.polygonPoints } : {}),
          ...(a.curvePoints?.length ? { curvePoints: a.curvePoints } : {}),
        })),
        stages: stageAssignments.value.map((s) => ({
          stageId: s.stageId,
          svgElementId: "",
          boundaryPath: s.boundaryPath,
          ...(s.boundaryPoints?.length
            ? { boundaryPoints: s.boundaryPoints }
            : {}),
          ...(s.curvePoints?.length ? { curvePoints: s.curvePoints } : {}),
        })),
        pointsOfInterest: pointsOfInterest.value,
        zoomLevels: zoomLevels.value,
        ...(estateZoomCenter.value
          ? { estateZoomCenter: estateZoomCenter.value }
          : existingMasterplanId.value
          ? { estateZoomCenter: null }
          : {}),
        ...(stageZoomCenter.value
          ? { stageZoomCenter: stageZoomCenter.value }
          : existingMasterplanId.value
          ? { stageZoomCenter: null }
          : {}),
        ...(lotNumberMetadata.value.length > 0
          ? { lotNumberMetadata: lotNumberMetadata.value }
          : {}),
        ...(lotSizeMetadata.value.length > 0
          ? { lotSizeMetadata: lotSizeMetadata.value }
          : {}),
        ...(stageLabelMetadata.value.length > 0
          ? { stageLabelMetadata: stageLabelMetadata.value }
          : {}),
        ...(estateBoundaryPath.value
          ? { estateBoundaryPath: estateBoundaryPath.value }
          : {}),
        ...(estateLabelMetadata.value
          ? { estateLabelMetadata: estateLabelMetadata.value }
          : {}),
        ...(coordinateViewBox ? { coordinateViewBox } : {}),
      };

      if (existingMasterplanId.value) {
        await interactiveMasterplanApi.updateMasterplan(
          existingMasterplanId.value,
          masterplanData
        );
      } else {
        await interactiveMasterplanApi.createMasterplan(masterplanData);
      }

      showSuccess.value = true;
    } catch (error) {
      console.error("Failed to save masterplan:", error);
      const err = error as { response?: { data?: { message?: string } } };

      alert(err.response?.data?.message || "Failed to save masterplan");
    } finally {
      saving.value = false;
    }
  };

  const confirmOverwrite = () => {
    showOverwriteConfirm.value = false;
  };

  const editExisting = async () => {
    showOverwriteConfirm.value = false;
    loadingExisting.value = true;
    const data = existingMasterplanData.value;

    if (!data) {
      loadingExisting.value = false;
      return;
    }

    if (!estateLots.value.length) {
      try {
        const lotsResponse = await lotApi.getEstateLots({
          estateId: estateId.value,
          params: {},
        });

        if (Array.isArray(lotsResponse.data)) {
          estateLots.value = mapLots(lotsResponse.data);
        } else if (
          lotsResponse.data?.data &&
          Array.isArray(lotsResponse.data.data)
        ) {
          estateLots.value = mapLots(lotsResponse.data.data);
        }
      } catch (err) {
        console.error("Failed to load estate lots:", err);
      }
    }

    const toId = (val: any): string =>
      val && typeof val === "object" ? val._id?.toString() ?? "" : val ?? "";

    pointsOfInterest.value = data.pointsOfInterest || [];
    zoomLevels.value = {
      estateBoundary: data.zoomLevels?.estateBoundary ?? 1,
      stages: data.zoomLevels?.stages ?? 2,
      lots: data.zoomLevels?.lots ?? 3,
      ...(data.zoomLevels?.mobile
        ? { mobile: { ...data.zoomLevels.mobile } }
        : {}),
      ...(data.zoomLevels?.mobileLandscape
        ? { mobileLandscape: { ...data.zoomLevels.mobileLandscape } }
        : {}),
    };
    estateZoomCenter.value = data.estateZoomCenter;
    stageZoomCenter.value = data.stageZoomCenter;
    initialStageBoundaries.value = (data.stages || []).map((s: any) => ({
      stageId: toId(s.stageId),
      boundaryPath: s.boundaryPath,
      boundaryPoints: s.boundaryPoints,
      curvePoints: s.curvePoints,
    }));

    const savedLotNumberMetadata: any[] = data.lotNumberMetadata || [];

    lotNumberMetadata.value = savedLotNumberMetadata;

    const savedLotSizeMetadata: any[] = data.lotSizeMetadata || [];

    lotSizeMetadata.value = savedLotSizeMetadata;

    const savedStageLabelMetadata: any[] = data.stageLabelMetadata || [];

    stageLabelMetadata.value = savedStageLabelMetadata;
    initialStageLabelMetadata.value = savedStageLabelMetadata;

    estateBoundaryPath.value = data.estateBoundaryPath || null;
    initialEstateBoundaryPath.value = data.estateBoundaryPath || undefined;

    if (data.estateLabelMetadata) {
      estateLabelMetadata.value = data.estateLabelMetadata;
      initialEstateLabelMetadata.value = { ...data.estateLabelMetadata };
    }

    const reconstructedAssignments: Assignment[] = (data.lots || []).map(
      (l: any) => {
        const lotId = toId(l.lotId);
        const meta = savedLotNumberMetadata.find(
          (m: any) => String(m.lotId) === lotId
        );
        const sizeMeta = savedLotSizeMetadata.find(
          (m: any) => String(m.lotId) === lotId
        );

        return {
          elementIndex: l.elementIndex,
          type: l.polygonPoints?.length ? "polygon" : "rect",
          lotId,
          boundingBox: l.boundingBox,
          ...(l.shapePath ? { shapePath: l.shapePath } : {}),
          ...(l.statusBarPath ? { statusBarPath: l.statusBarPath } : {}),
          ...(l.polygonPoints ? { polygonPoints: l.polygonPoints } : {}),
          ...(l.curvePoints?.length ? { curvePoints: l.curvePoints } : {}),
          ...(meta ? { lotNumberMetadata: meta } : {}),
          ...(sizeMeta ? { lotSizeMetadata: sizeMeta } : {}),
        };
      }
    );

    initialAssignments.value = reconstructedAssignments;
    assignments.value = reconstructedAssignments;

    enrichedAssignments.value = (data.lots || []).map((l: any) => ({
      elementIndex: l.elementIndex,
      type: l.polygonPoints?.length ? "polygon" : "rect",
      lotId: toId(l.lotId),
      boundingBox: l.boundingBox,
      stageId: toId(l.stageId),
      ...(l.shapePath ? { shapePath: l.shapePath } : {}),
      ...(l.statusBarPath ? { statusBarPath: l.statusBarPath } : {}),
      ...(l.polygonPoints ? { polygonPoints: l.polygonPoints } : {}),
      ...(l.curvePoints?.length ? { curvePoints: l.curvePoints } : {}),
    }));

    stageAssignments.value = initialStageBoundaries.value;

    try {
      const svgUrl = data.svgFile?.url;

      if (svgUrl) {
        const response = await fetch(svgUrl);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch SVG: ${response.status} ${response.statusText}`
          );
        }

        const contentType = response.headers.get("content-type") || "";

        if (
          !contentType.includes("svg") &&
          !contentType.includes("xml") &&
          !contentType.includes("octet-stream")
        ) {
          throw new Error(`Unexpected content-type for SVG: ${contentType}`);
        }

        const blob = await response.blob();
        const svgFile = new File(
          [blob],
          data.svgFile.fileName || "masterplan.svg",
          { type: "image/svg+xml" }
        );

        const elements: SvgElement[] = reconstructedAssignments.map((a) => ({
          elementIndex: a.elementIndex,
          type: a.type as "rect" | "path" | "polygon" | "circle",
          boundingBox: a.boundingBox,
          shapePath: a.shapePath,
          statusBarPath: a.statusBarPath,
          polygonPoints: a.polygonPoints,
          curvePoints: a.curvePoints,
          rawAttributes: {},
        }));

        uploadResult.value = { svgFile, elements };
      }
    } catch (err) {
      console.error("Failed to fetch existing SVG:", err);
      loadingExisting.value = false;
      alert("Could not load existing SVG file. Please re-upload it.");
      return;
    }

    loadingExisting.value = false;
    step.value = "match";
  };

  const startFresh = () => {
    showOverwriteConfirm.value = false;
    initialAssignments.value = [];
    initialStageBoundaries.value = [];
    assignments.value = [];
    enrichedAssignments.value = [];
    pointsOfInterest.value = [];
    zoomPreviewState.value = undefined;
    zoomLevels.value = { estateBoundary: 1, stages: 2, lots: 3 };
    estateZoomCenter.value = undefined;
    stageZoomCenter.value = undefined;
    lotNumberMetadata.value = [];
    lotSizeMetadata.value = [];
    uploadResult.value = null;
    step.value = "upload";
  };

  const cancelOverwrite = () => {
    showOverwriteConfirm.value = false;
    existingMasterplanId.value = null;
    router.push("/admin/estates");
  };

  const handleCancel = () => {
    if (
      confirm("Are you sure you want to cancel? All progress will be lost.")
    ) {
      router.push("/admin/estates");
    }
  };

  onMounted(async () => {
    if (estateId.value) {
      try {
        const [estateResponse, stagesResponse] = await Promise.all([
          estateApi.getEstate(estateId.value),
          stageApi.getStages({ estateId: estateId.value }),
        ]);

        selectedEstate.value = estateResponse.data;

        const stagesData = stagesResponse.data;

        if (Array.isArray(stagesData)) {
          estateStages.value = stagesData;
        } else if (stagesData?.data && Array.isArray(stagesData.data)) {
          estateStages.value = stagesData.data;
        } else {
          estateStages.value = [];
        }

        try {
          const existingResponse =
            await interactiveMasterplanApi.getMasterplanByEstateId(
              estateId.value
            );

          if (existingResponse.data?._id) {
            existingMasterplanId.value = existingResponse.data._id;
            existingMasterplanData.value = existingResponse.data;
            showOverwriteConfirm.value = true;
          }
        } catch (error: any) {
          if (error?.response?.status === 404) {
            console.log("No previous masterplan found");
          } else {
            console.error(
              "Unexpected error loading existing masterplan:",
              error
            );
            alert("Failed to check for existing masterplan. Please try again.");
            return;
          }
        }
      } catch (error) {
        console.error("Failed to load estate or stages:", error);
        alert("Failed to load estate");
        router.push("/admin/estates");
      }
    }
  });

const __returned__ = { router, route, store, estateId, wizardSteps, step, currentStepIndex, goToPreviousStep, selectedEstate, uploadResult, assignments, estateLots, estateStages, stageAssignments, enrichedAssignments, pointsOfInterest, lotNumberMetadata, lotSizeMetadata, stageLabelMetadata, saving, showSuccess, existingMasterplanId, showOverwriteConfirm, existingMasterplanData, loadingExisting, initialAssignments, initialStageBoundaries, initialStageLabelMetadata, estateBoundaryPath, initialEstateBoundaryPath, estateLabelMetadata, initialEstateLabelMetadata, zoomLevels, estateZoomCenter, stageZoomCenter, mapLots, handleUploadSuccess, handleAssignmentComplete, handleStageComplete, handleEstateBoundaryComplete, handlePoiComplete, zoomPreviewState, handleZoomComplete, handleSave, confirmOverwrite, editExisting, startFresh, cancelOverwrite, handleCancel, computed, ref, onMounted, get useRouter() { return useRouter }, get useRoute() { return useRoute }, get useStore() { return useStore }, MasterplanUpload, LotMatcher, StageDrawer, PointsOfInterestEditor, EstateBoundaryDrawer, ZoomLevelsEditor, get interactiveMasterplanApi() { return interactiveMasterplanApi }, get estateApi() { return estateApi }, get lotApi() { return lotApi }, get stageApi() { return stageApi }, get EstateType() { return EstateType } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})